/**
 * The training loop: collect experience, run PPO on it, repeat.
 *
 * ===========================================================================
 * WHAT THE TRAINER IS AND IS NOT
 * ===========================================================================
 *
 * It owns the network, the optimizer and the statistics, and it drives one
 * `RolloutBackend`. It does NOT know how experience is collected — Node uses
 * worker threads, a browser uses Web Workers, and a test uses the synchronous
 * in-process collector. All three implement `RolloutBackend`, and swapping them
 * changes nothing above this line. That is what lets the same `Trainer` run
 * headless in the CLI and inside a tab with a live chart attached.
 *
 * ===========================================================================
 * A RUN IS REPRODUCIBLE FROM (seed, iteration)
 * ===========================================================================
 *
 * Every stochastic choice derives from the master seed AND the iteration index,
 * never from a running generator that lives longer than one iteration:
 *
 *   - each chunk's environment seeds and action draws come from
 *     `mixSeed(iterationSeed, chunkIndex)`;
 *   - PPO's minibatch shuffle comes from a `PPO` object CONSTRUCTED FRESH each
 *     iteration with `mixSeed(config.seed, iteration)`.
 *
 * That second one is the load-bearing detail. `PPO` owns a private generator
 * that advances across `update` calls and is not part of a `Checkpoint`, so a
 * long-lived `PPO` would make a resumed run shuffle differently from a straight
 * one and the parameters would diverge — quietly, and only after a resume.
 * Rebuilding the object per iteration ties the shuffle to a number that IS
 * checkpointed (`TrainingStats.iteration`), and `test/rl/trainer.test.ts`
 * asserts 10 iterations straight and 5 + checkpoint + 5 give BIT-IDENTICAL
 * parameters. Rebuilding costs a handful of small typed arrays per iteration.
 *
 * ONE OPTIONAL EXCEPTION, AND IT IS SPELLED OUT. With
 * `parallelGradients` on, the PPO minibatch gradient is summed in pieces across
 * the worker pool, and a sum of doubles is not associative — so the parameters
 * depend on the SHARD COUNT as well as on (seed, iteration). A run reproduces
 * itself exactly at a fixed worker count and reproduces the serial path only to
 * about 1e-9 relative. That is why the flag is off by default and why the
 * resume test above runs without it. See ./gradients.
 *
 * For the same reason every collection starts its environments from a fresh
 * `reset()` rather than continuing an episode across the iteration boundary: an
 * in-flight episode is simulation state that a checkpoint cannot hold, so
 * carrying it would put a resumed run on a different trajectory. The cost is
 * the tail of one episode per chunk per iteration, and with randomized initial
 * states (see ../rl/env) a truncated tail is not a state the policy needed.
 *
 * ===========================================================================
 * SELF-PLAY, AND WHY THERE IS AN OPPONENT POOL
 * ===========================================================================
 *
 * The observation is mirrored (see ./obs §2), so ONE network plays both sides
 * and every car in the match generates on-policy data. That is the default and
 * it is the cheapest way to double the sample rate.
 *
 * Pure self-play also has a well-known failure: two identical policies can
 * settle into a joint behaviour that neither has any gradient to leave — most
 * often both cars stalling near their own goal, where nothing happens, nobody
 * concedes, and the dense terms are the only signal left. `opponent: 'pool'`
 * plays the learner against a RING OF FROZEN PAST SNAPSHOTS, and
 * `opponent: 'scripted'` plays it against the project's own bot at a tier.
 * Both break the symmetry: the opponent no longer moves when the learner does.
 *
 * ===========================================================================
 * THE CURRICULUM IS DRIVEN FROM HERE, AND MEASURED
 * ===========================================================================
 *
 * ./env owns the state distributions; this file owns the SCHEDULE, because the
 * schedule is a function of what the last iteration measured and the
 * environment cannot see that. One number, `difficulty` in [0, 1], goes down to
 * every collector on every `collect`, and `nextDifficulty` moves it from the
 * EPISODE-level touch rate the iteration produced — see `CurriculumConfig` for
 * why that is not the same number as `IterationStats.touchRate` and why using
 * the latter deadlocked the schedule. It is part of `TrainingStats` for the same
 * reason the Adam moments are part of a `Checkpoint`: a resume that restarted
 * the schedule would put a trained policy back on states it long ago outgrew.
 *
 * Without it, the whole loop is correct and learns nothing visible — from a
 * full-difficulty reset a fresh policy never reaches the ball, so the touch
 * rate and every reward term downstream of a touch stay at zero for as long as
 * anyone is willing to watch. See the header of ./env.
 *
 * ===========================================================================
 * THE ONE PLACE A CLOCK IS READ
 * ===========================================================================
 *
 * `TrainingStats.totalSeconds` and `TrainerProgress.stepsPerSecond` are
 * wall-clock quantities, so this file reads `Date.now`. Nothing that affects a
 * parameter reads it — the timing is written into the statistics and never back
 * into the training math, which is why resume equivalence survives a machine
 * that runs at a different speed. `test/sim/decoupling.test.ts` pins the set of
 * files under src/rl allowed to touch a clock at exactly this one and
 * ./storage.
 */

import { Rng } from '../physics/rng';
import type { ScriptedTier } from '../bot/difficulty';
import { ACTION_COUNT } from './actions';
import { RLEnv, type AgentController, type ResetMode } from './env';
import {
  Adam,
  applyCheckpoint,
  logSoftmax,
  makeForwardResult,
  MlpPolicy,
  sampleCategorical,
  toCheckpoint,
} from './nn';
import {
  allocateGradientBuffers,
  publishBatch,
  reduceGradientShards,
  sharedMemoryAvailable,
  type GradientBuffers,
  type GradientRequest,
  type GradientSetupRequest,
} from './gradients';
import { DEFAULT_PPO_CONFIG, PPO, ReturnNormalizer, buildBatch } from './ppo';
import type {
  AdvantageNorm,
  GradientSessionSpec,
  GradientShards,
  LossReport,
} from './ppo';
import { assertCheckpointCompatible } from './policy';
import { DEFAULT_REWARD_WEIGHTS, REWARD_TERM_NAMES } from './reward';
import { HISTORY_LIMIT, OBS_SIZE } from './types';
import type {
  ActionId,
  Batch,
  Checkpoint,
  ForwardResult,
  IterationStats,
  PPOConfig,
  Transition,
  TrainerPhase,
  TrainerProgress,
  TrainingConfig,
  TrainingStats,
} from './types';

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

export type OpponentKind =
  /** The learning policy drives every car on both teams. */
  | 'self'
  /** The learning policy drives blue; the project's scripted bot drives orange. */
  | 'scripted'
  /** The learning policy drives blue; a frozen past snapshot drives orange. */
  | 'pool';

export interface OpponentConfig {
  kind: OpponentKind;
  /** Tier for `kind: 'scripted'`. Defaults to 'pro'. */
  tier?: ScriptedTier;
  /** Iterations between pool snapshots, for `kind: 'pool'`. */
  refreshIterations?: number;
  /** How many snapshots the pool retains. Bounded: each one is a full
   *  parameter vector, and an unbounded pool grows without limit. */
  size?: number;
}

/**
 * Fields the shared `TrainingConfig` does not carry.
 *
 * `TrainingConfig` in ./types is the contract every part of the stack agrees
 * on and is deliberately not extended there. These are trainer-side choices,
 * declared as an intersection so a plain `TrainingConfig` is still a legal
 * argument everywhere and the UI does not have to know they exist.
 */
export interface TrainingConfigExtras {
  opponent?: OpponentConfig;
  /**
   * Shard each PPO minibatch's gradient over the rollout workers instead of
   * computing it on this thread alone. OFF BY DEFAULT, and deliberately.
   *
   * The update is 86-95 % of an iteration and the workers are idle for all of
   * it, so this is where the throughput is — but the reduction re-associates a
   * sum of doubles, so the parameters differ from the serial path's in their
   * last bits. Three tests rest on bit-identity and every one of them runs the
   * default. See ./gradients for what the fast path does and does not promise;
   * the short version is that it is reproducible with ITSELF at a fixed worker
   * count, and agrees with the serial path to a relative error below 1e-9.
   *
   * Needs a backend that offers it: the Node worker pool with two or more
   * threads. The synchronous backend has nothing to shard onto and the browser
   * one has no `SharedArrayBuffer` without cross-origin isolation, so both
   * ignore this and run the serial update.
   */
  parallelGradients?: boolean;
  /** Cars per team. Default 1. */
  teamSize?: number;
  /** Ticks each action is held. Defaults to the env's frame skip. */
  frameSkip?: number;
  /** How episodes start. Defaults to 'curriculum'. */
  resetMode?: ResetMode;
  /** How the curriculum advances, for `resetMode: 'curriculum' | 'nearBall'`. */
  curriculum?: CurriculumConfig;
}

/**
 * The curriculum schedule.
 *
 * DRIVEN BY MEASUREMENT, NOT BY ITERATION COUNT. A fixed ramp — "difficulty
 * 0.02 per iteration" — is a bet that the policy learns at a rate nobody has
 * checked, and when it does not, the states run away from it and the run
 * silently reverts to the flat curve the curriculum was added to fix. Here the
 * touch rate the last iteration actually produced decides: above
 * `advanceTouchRate` the states get harder, below `retreatTouchRate` they get
 * easier again, and in between nothing moves.
 *
 * The two thresholds are separate on purpose. A single threshold with a step on
 * each side oscillates around it, and the difficulty is a property of the DATA
 * DISTRIBUTION, so oscillating it means every batch disagrees with the last.
 * The gap between them is a deadband.
 *
 * ===========================================================================
 * WHICH TOUCH RATE. THIS IS THE BUG THAT DEADLOCKED THE FIRST VERSION
 * ===========================================================================
 *
 * The thresholds are read against `IterationStats.episodeTouchRate` — the
 * fraction of ENVIRONMENT episodes in which AT LEAST ONE learning agent touched
 * the ball — and NOT against `IterationStats.touchRate`, which is the fraction
 * of AGENT-episodes with a touch and is what the UI charts.
 *
 * The distinction is the whole fix. Under 1v1 self-play both cars start beside
 * the ball, one takes it, and the other agent's episode records no touch
 * however well it played, so the per-agent rate has a STRUCTURAL CEILING near
 * 1/nLearners plus whatever second touches happen to land. Measured, that
 * ceiling is about 0.5: a 100-iteration run oscillated between 0.31 and 0.50
 * and never reached the 0.6 the first version of this gate demanded, so
 * `difficulty` sat at 0.00 for the entire run and the policy was pinned to rung
 * one forever. A gate whose bar is above its metric's attainable maximum is not
 * a strict curriculum; it is a curriculum that cannot run. `CURRICULUM_STALL_WARNING`
 * and `assertCurriculumCanAdvance` exist so the next one of these is loud.
 *
 * The episode-level measure has a ceiling of 1.0 for ANY roster size, because
 * one competent car is enough to satisfy it. That is the property a promotion
 * gate needs: the bar has to sit somewhere the metric can actually reach.
 *
 * Two alternatives were considered and rejected. Dividing the per-agent rate by
 * the learner count re-scales the same noisy number without changing what it
 * measures — an episode where one car does everything and one car is asleep
 * scores the same as one where both play half a duel. Gating on return
 * improvement makes the schedule depend on the reward WEIGHTS, so re-weighting
 * a term silently re-tunes the curriculum; the touch is the one event that
 * means the same thing under every weighting.
 */
export interface CurriculumConfig {
  /** Which controller moves the difficulty. See `CurriculumMode`. */
  mode?: CurriculumMode;
  /** Where difficulty starts on a fresh run. */
  start?: number;
  /** Episode touch rate at or above which difficulty steps up. `gated` only. */
  advanceTouchRate?: number;
  /** Episode touch rate at or below which it steps back down. `gated` only. */
  retreatTouchRate?: number;
  /** How far difficulty moves in one iteration. `gated` only. */
  step?: number;
  /** Iterations before the first move, so the first estimate is not acted on
   *  while the value head is still random. `gated` only. */
  warmupIterations?: number;
  /** Hard ceiling. Below 1 pins a run to a slice of the schedule, which is what
   *  a test wants and a real run does not. Also the top of the `scheduled`
   *  ramp. */
  maxDifficulty?: number;
  /**
   * `scheduled` only: agent-steps over which the ramp runs from `start` to
   * `maxDifficulty`. Counted in `TrainingStats.totalSteps`, which is what a
   * checkpoint carries, so a resumed run rejoins the ramp where it left off
   * rather than restarting it.
   */
  rampSteps?: number;
  /** `scheduled` only: how the ramp is shaped between its endpoints. */
  shape?: CurriculumShape;
}

/**
 * WHICH CONTROLLER MOVES THE DIFFICULTY.
 *
 *   gated      the measured ratchet above: promote on a touch rate over the
 *              advance bar, retreat under the retreat bar, hold in between.
 *   scheduled  difficulty as a function of steps consumed. No metric, so it
 *              cannot random-walk and cannot stall — and no feedback, so it can
 *              outrun the policy.
 *
 * The two failure modes are opposites, which is why both are kept. The gated
 * one was measured over four runs and one 5.5M-step ramp: driven by a metric
 * whose standard error is about 0.10 and whose observed range is 0.00 to 0.79,
 * a bang-bang controller random-walks whatever its thresholds, and the only
 * setting that produced a monotone ramp at all was `retreatTouchRate` below 0,
 * i.e. a ratchet that cannot give ground. That ratchet then plateaued at
 * difficulty 0.26, because a ratchet still only climbs on iterations whose
 * NOISE clears the bar.
 *
 * A scheduled ramp trades that for the opposite risk. It always arrives, on
 * time, at a difficulty nobody checked the policy can handle; if it outruns the
 * policy the symptom is touch rate and return falling as `difficulty` rises,
 * which is visible in the same two columns the gated one is diagnosed from.
 * `scripts/train.ts` prints both every iteration.
 */
export type CurriculumMode = 'gated' | 'scheduled';

/**
 * The shape of a `scheduled` ramp between `start` and `maxDifficulty`.
 *
 *   linear  constant difficulty per step.
 *   cosine  slow at both ends. The easy end is where a fresh policy has nothing
 *           but accidental touches to learn from, and the hard end is where the
 *           states stop resembling each other, so the ramp spends its steps
 *           there and crosses the middle quickly.
 */
export type CurriculumShape = 'linear' | 'cosine';

/**
 * Defaults measured on the metric they are actually read against.
 *
 * Every number here was re-measured after the gate moved from the per-agent
 * touch rate to the episode one. The old ones were tuned against a metric whose
 * ceiling sat below its own advance bar, and NOTHING measured under those
 * conditions transfers — including the earlier finding that lowering the bar
 * hurt, which was a measurement of a schedule that could only ever retreat.
 *
 * The band, measured on the shipped configuration so it is the band that
 * matters: 1v1 self-play, seed 11, 4096 steps/iteration, 2400-tick episodes,
 * `step` forced to 0 so the difficulty could not move and the spread is the
 * metric's own rather than the schedule's. Over 60 iterations
 * `episodeTouchRate` ran 0.125 to 0.889 with a mean of 0.65, rising to a mean
 * of 0.72 over the last twenty as the policy improved. The per-agent
 * `touchRate` on the SAME iterations ran 0.063 to 0.556, mean 0.38 — the
 * structural ceiling, visible side by side.
 *
 * So `advanceTouchRate` is 0.7: above the early mean, below the 0.889 the
 * measure demonstrably reaches, and far below its attainable 1.0. A bar at 0.85
 * was tried first and is the same mistake in a smaller size — the metric
 * cleared it on 5 of 60 iterations while falling below the retreat bar on 15, so
 * the difficulty would have drifted DOWN.
 *
 * `retreatTouchRate` at 0.4 sits below all but the worst iterations, so the
 * schedule gives ground when a promotion genuinely outran the policy rather
 * than on ordinary noise. An iteration closes only 8 to 18 environment
 * episodes, so the measure is quantized in steps of about 0.1 and wobbles ±0.15
 * between neighbours; the 0.30 deadband is wider than that wobble on purpose.
 * The point is not that the difficulty never moves down — it is that a single
 * unlucky batch does not move it.
 *
 * `step` stays at 0.03: 34 promotions to cross the whole schedule, so a ramp is
 * a hundred-odd iterations rather than ten. Difficulty is a property of the DATA
 * DISTRIBUTION, and it has to move slowly enough that consecutive batches still
 * describe the same problem.
 *
 * WHAT THESE NUMBERS MEAN FOR A RUN. The schedule is a controller, not a ramp:
 * it settles where promotions and retreats balance, which for this band is an
 * episode touch rate near 0.57, and it climbs from there only when the policy
 * genuinely improves. Measured over 300 iterations of the shipped
 * configuration, the difficulty holds between 0.00 and 0.12 and is above zero on
 * about nine iterations in ten — against 0.00 on ALL of them before the fix.
 * A difficulty that has stopped climbing is now a statement about the policy;
 * before, it was a statement about the gate.
 *
 * DO NOT SMOOTH THE GATE METRIC. It is tempting: an iteration measures only 8
 * to 18 episodes, so a single reading is a coarse estimate of the policy's true
 * rate, and averaging the last few iterations would obviously tighten it. It
 * would also stop the schedule working. The true rate of a fresh policy at
 * difficulty 0 is about 0.55 — measured at 16,384 steps/iteration, where an
 * iteration closes 33 episodes and the estimate is four times tighter — which
 * is INSIDE the deadband. A tight estimator therefore reads "hold" every
 * iteration and the difficulty never moves until the policy crosses 0.7 on its
 * own. The noisy one crosses the bar on its lucky iterations, the difficulty
 * probes upward, and it is pushed back if the policy cannot hold the new rung.
 * The variance is the exploration; the deadband and the small `step` are what
 * keep it from becoming oscillation.
 */
export const DEFAULT_CURRICULUM: Required<CurriculumConfig> = {
  mode: 'gated',
  start: 0,
  advanceTouchRate: 0.7,
  retreatTouchRate: 0.4,
  step: 0.03,
  warmupIterations: 2,
  maxDifficulty: 1,
  rampSteps: 24_000_000,
  shape: 'cosine',
};

/**
 * A `scheduled` curriculum's position, given the steps consumed. Pure, so the
 * whole ramp is a unit test rather than a claim about a run.
 *
 * Reads `totalSteps` rather than an iteration count so that the ramp means the
 * same thing at any `stepsPerIteration`, and so that a resume rejoins it: the
 * step total is the one quantity a checkpoint carries that measures how much
 * experience the policy has actually had.
 */
export const scheduledDifficulty = (
  totalSteps: number,
  config: CurriculumConfig = {}
): number => {
  const c = { ...DEFAULT_CURRICULUM, ...config };
  // A non-positive ramp is "already there" rather than a division by zero: it
  // is what `--ramp-steps 0` should mean, and it keeps the function total.
  const t =
    c.rampSteps > 0 ? Math.min(1, Math.max(0, totalSteps / c.rampSteps)) : 1;
  const shaped = c.shape === 'cosine' ? 0.5 - 0.5 * Math.cos(Math.PI * t) : t;
  const top = Math.min(1, Math.max(0, c.maxDifficulty));
  const bottom = Math.min(top, Math.max(0, c.start));
  return bottom + (top - bottom) * shaped;
};

/**
 * The curriculum's next position, given where it is and what was measured.
 * Pure, so the schedule is a unit test rather than a claim about a run.
 *
 * `episodeTouchRate` is the EPISODE-level rate, not the per-agent one — see
 * `CurriculumConfig`. Passing the wrong one is the original bug, and the reason
 * `assertCurriculumCanAdvance` exists.
 */
export const nextDifficulty = (
  current: number,
  episodeTouchRate: number,
  iteration: number,
  config: CurriculumConfig = {}
): number => {
  const c = { ...DEFAULT_CURRICULUM, ...config };
  if (iteration < c.warmupIterations) return current;
  const moved =
    episodeTouchRate >= c.advanceTouchRate
      ? current + c.step
      : episodeTouchRate <= c.retreatTouchRate
        ? current - c.step
        : current;
  return Math.min(c.maxDifficulty, Math.max(0, moved));
};

/**
 * Iterations of a pinned difficulty after which a run is reporting a problem
 * rather than a hard curriculum.
 *
 * A promotion gate that is never met produces no error and no missing output —
 * it produces a long run that ends with a ball-chaser, which is exactly how the
 * original bug survived a 100-iteration test run. `TrainingStats.curriculumStall`
 * counts iterations since the difficulty last ROSE; scripts/train.ts prints a
 * warning when it crosses this, and the training UI shows the counter beside the
 * difficulty. Neither reads a clock, so both are safe here.
 *
 * 25 rather than 5: at `step` 0.03 and a metric that wobbles, a healthy run
 * still spends a handful of iterations between promotions.
 *
 * THIS COUNTER ALONE IS NOT ENOUGH — see `curriculumProgressStalled` below. It
 * detects "never promoted", and the failure that has actually cost runs is
 * "promoted constantly and went nowhere".
 */
export const CURRICULUM_STALL_WARNING = 25;

/**
 * Iterations compared against the preceding equal number when asking whether
 * the schedule is going anywhere. Long enough that a bang-bang gate's whole
 * oscillation fits inside a window — at `step` 0.03 a difficulty wandering
 * 0.00 to 0.06 completes many cycles in 200 iterations — and short enough that
 * a run reports the problem in minutes rather than hours.
 */
export const CURRICULUM_PROGRESS_WINDOW = 200;

/**
 * Difficulty the high-water mark must gain across a window to count as
 * progress. One `step` at the default 0.03: a schedule that cannot manage a
 * single net promotion per 200 iterations will not finish a ramp of 1.0.
 */
export const CURRICULUM_PROGRESS_MARGIN = 0.03;

/**
 * IS THE CURRICULUM GOING ANYWHERE? Pure, so a synthetic trace is a unit test.
 *
 * `curriculumStall` above counts iterations since the difficulty last ROSE, and
 * any promotion resets it. It is not silent on an oscillating schedule — the
 * run that random-walked inside [0.00, 0.06] for 2400 iterations tripped it
 * nine times. What it cannot do is say what was wrong. Its message is
 * "difficulty pinned at 0.00 for 25 iterations", which describes a PAUSE, and a
 * pause is a normal thing for a working curriculum to do; nothing in it
 * distinguishes 25 iterations of catching breath from 2400 iterations of going
 * nowhere, because the counter is reset by every promotion and a schedule
 * oscillating in place promotes constantly. The run above was caught by
 * evaluating a checkpoint by hand, after the guard had "fired" nine times.
 *
 * So the property tested here is NET PROGRESS over hundreds of iterations, not
 * time since the last promotion. The high-water mark of the recent window has
 * to beat the high-water mark of the window before it; a bang-bang controller
 * oscillating in place fails that however often it promotes. The two guards
 * answer different questions and both are worth having: "is it moving right
 * now" and "has it got anywhere".
 *
 * WHY OSCILLATION IS THE DEFAULT FAILURE, not a tuning accident. The gate reads
 * `episodeTouchRate` over roughly 27 agent-episodes at the shipped 4,096 steps
 * and 2,400-tick episodes, so its standard error near a rate of 0.5 is about
 * 0.10 and observed values swing between 0.0 and 0.79 iteration to iteration. A
 * bang-bang controller driven by a metric that noisy random-walks in units of
 * `step` whatever its thresholds are: lowering the advance bar from 0.70 to
 * 0.45 was measured and did not change the shape. The controller is the
 * problem, not the threshold — a schedule that cannot retreat cannot random-walk
 * — which is what this guard exists to make visible before a run burns hours.
 *
 * The high-water mark rather than the mean because the mean is noisy in the
 * difficulty's own step size, and because "the hardest states this run has
 * reached" is the quantity that actually matters — a schedule that touches 0.5
 * and falls back has still shown the policy those states.
 *
 * Returns false while the run is still short of two full windows (nothing to
 * compare yet) and once the schedule is at its ceiling (arriving is not
 * stalling).
 */
export const curriculumProgressStalled = (
  difficulties: readonly number[],
  options: {
    window?: number;
    margin?: number;
    maxDifficulty?: number;
  } = {}
): boolean => {
  const window = Math.max(1, Math.floor(options.window ?? CURRICULUM_PROGRESS_WINDOW));
  const margin = options.margin ?? CURRICULUM_PROGRESS_MARGIN;
  const ceiling = options.maxDifficulty ?? DEFAULT_CURRICULUM.maxDifficulty;
  if (difficulties.length < 2 * window) return false;

  const recent = difficulties.slice(-window);
  const previous = difficulties.slice(-2 * window, -window);
  const recentPeak = Math.max(...recent);
  // Already there. A run that finished its ramp reports no progress forever and
  // is not stalled; it is done.
  if (recentPeak >= ceiling) return false;
  return recentPeak < Math.max(...previous) + margin;
};

/**
 * Throw unless `advanceTouchRate` is strictly below what the gate metric can
 * attain — the assertion whose absence let a deadlocked curriculum ship.
 *
 * `attainable` is 1 for the episode-level rate at any roster size, because one
 * learner touching the ball satisfies it. It is a parameter rather than a
 * constant so a caller measuring the ceiling empirically can check against what
 * it actually saw; `test/rl/trainer.test.ts` does both.
 */
export const assertCurriculumCanAdvance = (
  config: CurriculumConfig = {},
  attainable = 1
): void => {
  const c = { ...DEFAULT_CURRICULUM, ...config };
  if (c.mode === 'scheduled') {
    // A schedule has no gate to deadlock, so the thresholds are not read and
    // asserting on them would reject a legal config. What it CAN be given is a
    // ramp that never moves, which is the same failure in the other instrument.
    if (!(Number.isFinite(c.rampSteps) && c.rampSteps >= 0)) {
      throw new RangeError(
        `curriculum rampSteps ${c.rampSteps} must be a finite step count; the ramp could never run`
      );
    }
    if (!(c.start < c.maxDifficulty)) {
      throw new RangeError(
        `curriculum start ${c.start} is not below maxDifficulty ${c.maxDifficulty}; ` +
          'the ramp has nowhere to go'
      );
    }
    return;
  }
  if (!(c.advanceTouchRate < attainable)) {
    throw new RangeError(
      `curriculum advance bar ${c.advanceTouchRate} is not below the gate metric's ` +
        `attainable maximum ${attainable}; the difficulty could never rise`
    );
  }
  if (!(c.retreatTouchRate < c.advanceTouchRate)) {
    throw new RangeError(
      `curriculum retreat ${c.retreatTouchRate} must be below advance ${c.advanceTouchRate}, ` +
        'or the schedule has no deadband and oscillates every iteration'
    );
  }
};

export type TrainerConfig = TrainingConfig & TrainingConfigExtras;

/**
 * A config that trains. The PPO half is ./ppo's documented defaults; the rest
 * is sized so one iteration is a few seconds of a single core.
 *
 * `stepsPerIteration` counts AGENT-steps (transitions), not environment steps:
 * it is the size of the batch PPO sees, which is the number that matters for
 * the minibatch count and for `totalSteps`. A self-play 1v1 environment step
 * therefore contributes two of them.
 */
export const DEFAULT_TRAINING_CONFIG: TrainerConfig = {
  ppo: { ...DEFAULT_PPO_CONFIG },
  hiddenSizes: [256, 256],
  stepsPerIteration: 4096,
  workers: 4,
  episodeTicks: 2400,
  seed: 1,
  rewardWeights: { ...DEFAULT_REWARD_WEIGHTS },
  opponent: { kind: 'self' },
  // Off. See `TrainingConfigExtras.parallelGradients` — the fast path is not
  // bit-identical to this one, and the default is the one the determinism tests
  // pin.
  parallelGradients: false,
  teamSize: 1,
  // 'curriculum', not 'mixed'. From `mixed` resets an untrained policy never
  // reaches the ball, so every term but one is silent and the run does not
  // visibly learn — see the header of ./env. The schedule ENDS at `mixed`, so
  // this default costs a completed run nothing.
  resetMode: 'curriculum',
  curriculum: { ...DEFAULT_CURRICULUM },
};

/**
 * A config with every field optional, one level deeper than `Partial` reaches.
 * `Partial<TrainerConfig>` would still demand all nine PPO fields the moment
 * `ppo` was mentioned at all, which makes "the defaults but with a different
 * learning rate" a nine-line literal.
 */
export type PartialTrainingConfig = Partial<Omit<TrainerConfig, 'ppo'>> & {
  ppo?: Partial<PPOConfig>;
};

/** Deep-ish merge, so a caller can override one PPO field without restating
 *  the other eight. */
export const resolveConfig = (partial: PartialTrainingConfig = {}): TrainerConfig => ({
  ...DEFAULT_TRAINING_CONFIG,
  ...partial,
  ppo: { ...DEFAULT_TRAINING_CONFIG.ppo, ...partial.ppo },
  hiddenSizes: [...(partial.hiddenSizes ?? DEFAULT_TRAINING_CONFIG.hiddenSizes)],
  rewardWeights: { ...DEFAULT_TRAINING_CONFIG.rewardWeights, ...partial.rewardWeights },
  opponent: { kind: 'self', ...DEFAULT_TRAINING_CONFIG.opponent, ...partial.opponent },
  curriculum: { ...DEFAULT_CURRICULUM, ...partial.curriculum },
});

// ---------------------------------------------------------------------------
// Seeds
// ---------------------------------------------------------------------------

/**
 * Derive a sub-seed. Integer arithmetic end to end, like ../physics/rng, so a
 * worker thread and the main thread agree bit for bit.
 */
export const mixSeed = (seed: number, index: number): number =>
  (Math.imul(seed ^ 0x9e3779b9, 2654435761) ^ Math.imul(index + 1, 40503)) >>> 0;

/**
 * Split `total` steps over `chunks` collectors, largest remainders first.
 *
 * Deterministic and independent of how many workers happen to be idle, which is
 * what makes the synchronous and the threaded backends comparable element for
 * element: chunk k gets the same budget and the same seed in both.
 */
export const splitSteps = (total: number, chunks: number): number[] => {
  const out: number[] = [];
  const base = Math.floor(total / chunks);
  const extra = total - base * chunks;
  for (let i = 0; i < chunks; i++) out.push(base + (i < extra ? 1 : 0));
  return out;
};

// ---------------------------------------------------------------------------
// Episode statistics
// ---------------------------------------------------------------------------

/**
 * Everything about an iteration's rollouts that the transitions themselves do
 * not carry.
 *
 * `RolloutBackend.collect` returns `Transition[][]`, from which a return and an
 * episode length can be recovered but a touch rate, a goal count and a per-term
 * reward breakdown cannot — none of them is a field of `Transition`. A backend
 * reports them through the optional `episodeStats()` hook; one that does not
 * simply leaves those series at zero.
 */
export interface EpisodeStats {
  /** Per-agent episodes closed during the collection, truncated ones included. */
  episodes: number;
  returnSum: number;
  lengthTickSum: number;
  goalsFor: number;
  goalsAgainst: number;
  /** Episodes in which the agent touched the ball at least once. */
  touchedEpisodes: number;
  /**
   * ENVIRONMENT episodes closed, counted once each however many learners played
   * them. `episodes` above counts one per agent per episode, so under 1v1
   * self-play it is exactly twice this.
   *
   * The episode the step budget cut short counts, exactly as it does for
   * `episodes`. That biases the rate below DOWN — a half-length episode had
   * half the chance to produce a touch — by an amount that depends on
   * `stepsPerIteration` against `episodeTicks`. The thresholds in
   * `DEFAULT_CURRICULUM` were measured with the bias present, on the shipped
   * ratio; a run that changes that ratio a long way should re-measure.
   */
  envEpisodes: number;
  /**
   * Environment episodes in which AT LEAST ONE recorded learner touched the
   * ball. `envTouchedEpisodes / envEpisodes` is the curriculum's gate metric,
   * whose maximum is 1 for any roster size — see `CurriculumConfig` for why the
   * per-agent rate above cannot be used for that.
   */
  envTouchedEpisodes: number;
  /** Summed weighted contribution per reward term, over all episodes. */
  rewardTermSum: Record<string, number>;
}

export const emptyEpisodeStats = (): EpisodeStats => {
  const rewardTermSum: Record<string, number> = {};
  for (const name of REWARD_TERM_NAMES) rewardTermSum[name] = 0;
  return {
    episodes: 0,
    returnSum: 0,
    lengthTickSum: 0,
    goalsFor: 0,
    goalsAgainst: 0,
    touchedEpisodes: 0,
    envEpisodes: 0,
    envTouchedEpisodes: 0,
    rewardTermSum,
  };
};

const mergeEpisodeStats = (into: EpisodeStats, from: EpisodeStats): EpisodeStats => {
  into.episodes += from.episodes;
  into.returnSum += from.returnSum;
  into.lengthTickSum += from.lengthTickSum;
  into.goalsFor += from.goalsFor;
  into.goalsAgainst += from.goalsAgainst;
  into.touchedEpisodes += from.touchedEpisodes;
  // `?? 0`: a worker built from an older bundle answers without these two, and
  // a NaN here would silently poison the gate metric rather than fail.
  into.envEpisodes += from.envEpisodes ?? 0;
  into.envTouchedEpisodes += from.envTouchedEpisodes ?? 0;
  for (const [name, value] of Object.entries(from.rewardTermSum)) {
    into.rewardTermSum[name] = (into.rewardTermSum[name] ?? 0) + value;
  }
  return into;
};

/**
 * The curriculum's gate metric for one collection, with a fallback.
 *
 * `envEpisodes` is zero only when the backend predates the field, in which case
 * the per-agent rate is the only thing on offer — wrong, but wrong in the
 * direction of not promoting, which is the safe failure.
 */
export const gateTouchRate = (stats: EpisodeStats): number =>
  stats.envEpisodes > 0
    ? stats.envTouchedEpisodes / stats.envEpisodes
    : stats.touchedEpisodes / Math.max(1, stats.episodes);

// ---------------------------------------------------------------------------
// The backend seam
// ---------------------------------------------------------------------------

/**
 * Where experience comes from.
 *
 * CONTRACT. `collect` returns EXACTLY `steps` transitions in total, split into
 * trajectories that never span an episode boundary — the trainer computes GAE
 * per trajectory, and a trajectory containing two episodes leaks advantage
 * backwards across the join (see ./ppo §1). A trajectory that ends for any
 * reason OTHER than the episode ending — a time limit, or the step budget
 * running out — must carry `bootstrap` on its last transition; see
 * `Transition.done`.
 *
 * Same `params`, same `seed` and same `difficulty` must give the same
 * transitions, whichever backend produced them; `test/rl/trainer.test.ts`
 * asserts that for the synchronous and the worker-thread implementations.
 *
 * `difficulty` is the curriculum position the episodes are drawn at. It is a
 * parameter of `collect` rather than of the constructor because it changes
 * every iteration and a backend's environments are built once; a backend that
 * omits it leaves its environments wherever they were, which for a fixed reset
 * mode is the whole story.
 */
export interface RolloutBackend {
  collect(
    params: Float64Array,
    steps: number,
    seed: number,
    difficulty?: number
  ): Promise<Transition[][]>;
  dispose(): void;
  /** Optional. See `EpisodeStats` — the series `Transition` cannot express. */
  episodeStats?(): EpisodeStats | null;
  /** Optional. Freeze these parameters as the opponent, for `opponent: 'pool'`.
   *  A backend that only supports self-play may omit it. */
  setOpponent?(params: Float64Array | null): void;
  /** Optional. Set by the trainer; called with the running step count during a
   *  collection so a progress bar can move before the iteration ends. */
  onProgress?: (steps: number) => void;
  /**
   * Optional. Shard a PPO minibatch's gradient over the same workers that
   * collect, for `parallelGradients`.
   *
   * Null — and absent altogether — mean "no, run the serial update", which is
   * what the synchronous backend and the browser one both answer. A backend
   * that offers it must return the SAME object every call: the shard count is
   * part of the arithmetic, so a session that changed size mid-run would change
   * the answers mid-run. See ./gradients.
   */
  gradientShards?(): GradientShards | null;
}

// ---------------------------------------------------------------------------
// Collecting, in one process
// ---------------------------------------------------------------------------

/** Per-agent accumulators for the episode currently in flight. */
interface EpisodeAccumulator {
  returnSum: number;
  goalsFor: number;
  goalsAgainst: number;
  touched: boolean;
  terms: Record<string, number>;
  steps: number;
}

const newAccumulator = (): EpisodeAccumulator => ({
  returnSum: 0,
  goalsFor: 0,
  goalsAgainst: 0,
  touched: false,
  terms: {},
  steps: 0,
});

/**
 * One environment, one network, one deterministic stream of episodes.
 *
 * This is the unit a worker thread owns, and the unit the synchronous backend
 * runs N of in a loop. Both paths run THIS class, so "the parallel backend
 * agrees with the sequential one" is a claim about one implementation rather
 * than two that are meant to agree — the same reasoning as ../sim/rollout.
 */
export class RolloutCollector {
  readonly index: number;
  private readonly env: RLEnv;
  private readonly net: MlpPolicy;
  /** Non-null only for `opponent: 'pool'`. */
  private readonly opponentNet: MlpPolicy | null;
  /** Agent slots (indices into `env.agentCarIndices`) whose experience is kept. */
  private readonly recorded: readonly number[];
  /** Agent slots driven by the frozen opponent network. */
  private readonly frozen: readonly number[];

  private readonly out: ForwardResult;
  private readonly logProbs: Float64Array;
  private readonly probs: Float64Array;
  private readonly envRng = new Rng(0);
  private readonly actionRng = new Rng(0);
  private opponentReady = false;

  constructor(config: TrainerConfig, index: number) {
    this.index = index;

    const teamSize = Math.max(1, Math.floor(config.teamSize ?? 1));
    const opponent = config.opponent ?? { kind: 'self' };
    const controllers: AgentController[] = [];
    for (let team = 0; team < 2; team++) {
      for (let i = 0; i < teamSize; i++) {
        controllers.push(
          team === 1 && opponent.kind === 'scripted'
            ? { kind: 'scripted', tier: opponent.tier ?? 'pro' }
            : { kind: 'learner' }
        );
      }
    }

    this.env = new RLEnv({
      teamSize,
      controllers,
      episodeTicks: config.episodeTicks,
      resetMode: config.resetMode ?? 'curriculum',
      difficulty: config.curriculum?.start ?? DEFAULT_CURRICULUM.start,
      rewardWeights: config.rewardWeights,
      ...(config.frameSkip === undefined ? {} : { frameSkip: config.frameSkip }),
    });

    // Slots are parallel to `env.agentCarIndices`, which is ascending, and cars
    // are laid out blue first — so the first `teamSize` learner slots are the
    // ones whose experience trains the policy under a pool or scripted
    // opponent, and under self-play every slot does.
    const slots = this.env.agentCarIndices.map((_, slot) => slot);
    if (opponent.kind === 'pool') {
      this.recorded = slots.filter((s) => this.env.world.cars[this.env.agentCarIndices[s]].team === 0);
      this.frozen = slots.filter((s) => !this.recorded.includes(s));
    } else {
      this.recorded = slots;
      this.frozen = [];
    }

    this.net = new MlpPolicy(OBS_SIZE, ACTION_COUNT, config.hiddenSizes, 0);
    this.opponentNet =
      this.frozen.length > 0 ? new MlpPolicy(OBS_SIZE, ACTION_COUNT, config.hiddenSizes, 0) : null;
    this.out = makeForwardResult(ACTION_COUNT);
    this.logProbs = new Float64Array(ACTION_COUNT);
    this.probs = new Float64Array(ACTION_COUNT);
  }

  /** Install the frozen opponent's parameters. Until this is called the pool
   *  opponent mirrors the learner, which is what a fresh run wants anyway. */
  setOpponent(params: Float64Array | readonly number[] | null): void {
    if (this.opponentNet === null || params === null) {
      this.opponentReady = false;
      return;
    }
    this.opponentNet.setParameters(params);
    this.opponentReady = true;
  }

  /**
   * Roll out exactly `steps` transitions.
   *
   * Episodes start fresh on every call — see the file header for why that is a
   * requirement rather than a simplification.
   */
  collect(
    params: Float64Array | readonly number[],
    steps: number,
    seed: number,
    difficulty?: number
  ): { trajectories: Transition[][]; stats: EpisodeStats } {
    this.net.setParameters(params);
    if (difficulty !== undefined) this.env.setDifficulty(difficulty);
    const sub = mixSeed(seed, this.index);
    this.envRng.state = sub;
    this.actionRng.state = mixSeed(sub, 0x5bf0);

    const stats = emptyEpisodeStats();
    const trajectories: Transition[][] = [];
    if (steps <= 0) return { trajectories, stats };

    const agentCount = this.env.agentCount;
    const actions: ActionId[] = new Array(agentCount).fill(0);
    const open = new Map<number, Transition[]>();
    const accumulators = new Map<number, EpisodeAccumulator>();
    for (const slot of this.recorded) {
      open.set(slot, []);
      accumulators.set(slot, newAccumulator());
    }

    let observations = this.env.reset(this.envRng.nextUint32());
    let count = 0;
    // The episode in flight, at ENVIRONMENT granularity: did any recorded
    // learner touch the ball in it, and did any of its steps get recorded at
    // all. One pair for the whole roster, which is exactly what makes the gate
    // metric's ceiling 1 rather than 1/nLearners.
    let envTouched = false;
    let envRecorded = false;

    while (count < steps) {
      const pending: { slot: number; logProb: number; value: number }[] = [];
      for (let slot = 0; slot < agentCount; slot++) {
        const useFrozen = this.opponentReady && this.frozen.includes(slot);
        const net = useFrozen && this.opponentNet !== null ? this.opponentNet : this.net;
        net.forward(observations[slot], this.out);
        logSoftmax(this.out.logits, this.logProbs);
        for (let i = 0; i < this.probs.length; i++) this.probs[i] = Math.exp(this.logProbs[i]);
        const action = sampleCategorical(this.probs, this.actionRng);
        actions[slot] = action;
        pending.push({ slot, logProb: this.logProbs[action], value: this.out.value });
      }

      const result = this.env.step(actions);

      // A time limit is not a termination — see `Transition.done`. The value of
      // the state the episode was cut at is estimated with the LEARNING net,
      // which is the same net that produced `choice.value`, so the bootstrap is
      // on the same scale as everything else in the trajectory.
      const truncated = result.done && result.info.terminated === 'timeout';

      for (const slot of this.recorded) {
        if (count >= steps) break;
        const choice = pending[slot];
        const trajectory = open.get(slot);
        if (trajectory === undefined) continue;
        const transition: Transition = {
          obs: observations[slot],
          action: actions[slot],
          logProb: choice.logProb,
          reward: result.rewards[slot],
          value: choice.value,
          done: result.done && !truncated,
        };
        if (truncated) {
          this.net.forward(result.observations[slot], this.out);
          transition.bootstrap = this.out.value;
        }
        trajectory.push(transition);
        count++;
        envRecorded = true;
        envTouched = envTouched || result.info.touched[slot];

        const acc = accumulators.get(slot) as EpisodeAccumulator;
        acc.returnSum += result.rewards[slot];
        acc.goalsFor += result.info.goalsFor[slot];
        acc.goalsAgainst += result.info.goalsAgainst[slot];
        acc.touched = acc.touched || result.info.touched[slot];
        acc.steps++;
        for (const [name, value] of Object.entries(result.info.rewardTerms[slot])) {
          acc.terms[name] = (acc.terms[name] ?? 0) + value;
        }
      }

      observations = result.observations;

      if (result.done) {
        for (const slot of this.recorded) {
          this.flush(stats, accumulators, open, trajectories, slot, result.info.episodeTick);
        }
        if (envRecorded) {
          stats.envEpisodes++;
          if (envTouched) stats.envTouchedEpisodes++;
        }
        envRecorded = false;
        envTouched = false;
        if (count < steps) observations = this.env.reset(this.envRng.nextUint32());
      }
    }

    // Whatever is still in flight when the budget runs out is a real, if
    // truncated, episode. Dropping it would make an iteration whose budget is
    // smaller than an episode report no statistics at all. It is bootstrapped
    // for the same reason a time limit is: the episode did not end, the
    // collection did.
    for (const slot of this.recorded) {
      const trajectory = open.get(slot);
      const last = trajectory?.[trajectory.length - 1];
      if (last !== undefined && !last.done && last.bootstrap === undefined) {
        this.net.forward(observations[slot], this.out);
        last.bootstrap = this.out.value;
      }
      this.flush(stats, accumulators, open, trajectories, slot, this.env.episodeTick);
    }
    if (envRecorded) {
      stats.envEpisodes++;
      if (envTouched) stats.envTouchedEpisodes++;
    }
    return { trajectories, stats };
  }

  private flush(
    stats: EpisodeStats,
    accumulators: Map<number, EpisodeAccumulator>,
    open: Map<number, Transition[]>,
    trajectories: Transition[][],
    slot: number,
    episodeTick: number
  ): void {
    const trajectory = open.get(slot);
    if (trajectory !== undefined && trajectory.length > 0) {
      trajectories.push(trajectory);
      open.set(slot, []);
    }
    const acc = accumulators.get(slot);
    if (acc === undefined || acc.steps === 0) return;
    stats.episodes++;
    stats.returnSum += acc.returnSum;
    stats.lengthTickSum += episodeTick;
    stats.goalsFor += acc.goalsFor;
    stats.goalsAgainst += acc.goalsAgainst;
    if (acc.touched) stats.touchedEpisodes++;
    for (const [name, value] of Object.entries(acc.terms)) {
      stats.rewardTermSum[name] = (stats.rewardTermSum[name] ?? 0) + value;
    }
    accumulators.set(slot, newAccumulator());
  }
}

// ---------------------------------------------------------------------------
// The synchronous backend
// ---------------------------------------------------------------------------

/**
 * Collect in this thread, one chunk at a time.
 *
 * The reference the worker pool is tested against, the only backend a test
 * should use (spawning threads for a unit test is seconds of startup for
 * nothing), and the correct fallback in a browser with no Worker support.
 *
 * `chunks` defaults to `config.workers` so that a synchronous run and a
 * threaded run of the same config partition the work identically and can be
 * compared transition by transition.
 */
export const createSyncBackend = (
  config: TrainingConfig,
  chunks = Math.max(1, Math.floor(config.workers))
): RolloutBackend => {
  const resolved = config as TrainerConfig;
  const collectors = Array.from(
    { length: chunks },
    (_, i) => new RolloutCollector(resolved, i)
  );
  let stats: EpisodeStats | null = null;

  const backend: RolloutBackend = {
    async collect(params, steps, seed, difficulty) {
      const budgets = splitSteps(steps, collectors.length);
      const merged = emptyEpisodeStats();
      const out: Transition[][] = [];
      let done = 0;
      for (let i = 0; i < collectors.length; i++) {
        if (budgets[i] <= 0) continue;
        const result = collectors[i].collect(params, budgets[i], seed, difficulty);
        out.push(...result.trajectories);
        mergeEpisodeStats(merged, result.stats);
        done += budgets[i];
        backend.onProgress?.(done);
      }
      stats = merged;
      return out;
    },
    episodeStats: () => stats,
    setOpponent(params) {
      for (const collector of collectors) collector.setOpponent(params);
    },
    dispose() {
      /* nothing owns a resource here */
    },
  };
  return backend;
};

// ---------------------------------------------------------------------------
// The worker-thread backend
// ---------------------------------------------------------------------------

/** What a worker is constructed with. */
export interface RolloutWorkerInit {
  config: TrainerConfig;
  index: number;
}

/** One collection request. */
export interface RolloutWorkerRequest {
  /** Absent, and optional, so that the rollout request keeps the shape it has
   *  always had while still discriminating against the gradient ones. */
  kind?: 'rollout';
  params: Float64Array;
  steps: number;
  seed: number;
  opponent: Float64Array | null;
  /** Curriculum position. Optional so a worker built against an older request
   *  shape still runs — it simply stays where it was. */
  difficulty?: number;
}

/** Everything a pooled worker will answer. See ./rollout-worker. */
export type RolloutWorkerMessage =
  | RolloutWorkerRequest
  | GradientSetupRequest
  | GradientRequest;

export interface RolloutWorkerResponse {
  /** Present only on a gradient reply; a rollout answer has no kind, for the
   *  same reason its request does not. */
  kind?: 'grad' | 'grad-setup';
  trajectories?: Transition[][];
  stats?: EpisodeStats;
  /** Partial loss report for a gradient shard. */
  report?: LossReport;
  /** The thread this ran on. The only honest evidence the pool is parallel. */
  threadId?: number;
  error?: string;
}

export interface WorkerRolloutBackend extends RolloutBackend {
  /** Distinct thread ids seen so far. `test/rl/trainer.test.ts` asserts there
   *  are as many as there are workers, which is what "genuinely parallel"
   *  means that a wall-clock speed-up measurement cannot say reliably in CI. */
  readonly threadIds: readonly number[];
  /** Null when this build cannot share memory between threads, or when there is
   *  only one worker and so nothing to shard onto. */
  gradientShards(): GradientShards | null;
}

/**
 * `import()` with a specifier the bundler cannot see.
 *
 * ./trainer is imported by the training UI, so it is part of the BROWSER
 * bundle. A static `import ... from 'node:worker_threads'` there is a build
 * failure, and even a literal dynamic import is followed by Rollup. Routing
 * every Node-only module through one opaque specifier keeps the browser build
 * clean while Node still resolves it at runtime, and the types are recovered
 * with `typeof import(...)`, which is erased.
 */
const nodeImport = <T>(specifier: string): Promise<T> =>
  import(/* @vite-ignore */ specifier) as Promise<T>;

interface NodeModules {
  fs: typeof import('node:fs');
  os: typeof import('node:os');
  path: typeof import('node:path');
  url: typeof import('node:url');
  crypto: typeof import('node:crypto');
  threads: typeof import('node:worker_threads');
}

const loadNode = async (): Promise<NodeModules> => {
  const [fs, os, path, url, crypto, threads] = await Promise.all([
    nodeImport<NodeModules['fs']>('node:fs'),
    nodeImport<NodeModules['os']>('node:os'),
    nodeImport<NodeModules['path']>('node:path'),
    nodeImport<NodeModules['url']>('node:url'),
    nodeImport<NodeModules['crypto']>('node:crypto'),
    nodeImport<NodeModules['threads']>('node:worker_threads'),
  ]);
  return { fs, os, path, url, crypto, threads };
};

/**
 * Source trees the worker bundle depends on. Mirrors ../sim/parallel, plus the
 * two the RL worker adds: a stale bundle that misses an edit to ./reward would
 * train against a reward function the main thread does not have.
 */
const WORKER_SOURCE_DIRS = ['physics', 'sim', 'game', 'bot', 'rl'];

let cachedBundle: { key: string; url: string } | null = null;

/**
 * Bundle ./rollout-worker into a single `.mjs` and return its URL.
 *
 * Same trick and same reason as ../sim/parallel: the core is TypeScript written
 * for a bundler, its imports have no file extensions, and Node's ESM resolver
 * rejects that outright — so a `Worker` cannot be pointed at the `.ts` file.
 * esbuild is already in the tree as vite's own dependency. The bundle is keyed
 * on the newest mtime across the source trees and cached in the OS temp
 * directory, so the cost is paid once per edit rather than once per run.
 */
const workerBundleUrl = async (): Promise<string> => {
  const { fs, os, path, url, crypto } = await loadNode();

  // `import.meta.url` is this file's own location when it is loaded as source —
  // in vitest, or from a `.ts`-aware runtime. The CLI, however, is itself
  // esbuild-bundled, and esbuild rewrites `import.meta.url` to the BUNDLE's
  // location, which is nowhere near src/rl. So the entry point is looked up
  // rather than assumed, and the working directory is the fallback.
  const beside = path.dirname(url.fileURLToPath(import.meta.url));
  const here = [beside, path.join(process.cwd(), 'src', 'rl')].find((dir) =>
    fs.existsSync(path.join(dir, 'rollout-worker.ts'))
  );
  if (here === undefined) {
    throw new Error(
      `cannot find rollout-worker.ts beside ${beside} or under ${process.cwd()}/src/rl; ` +
        'run the worker backend from the project root, or use createSyncBackend'
    );
  }
  const srcRoot = path.dirname(here);

  const hash = crypto.createHash('sha1');
  const walk = (dir: string): void => {
    if (!fs.existsSync(dir)) return;
    for (const entry of fs.readdirSync(dir).sort()) {
      const full = path.join(dir, entry);
      const stat = fs.statSync(full);
      if (stat.isDirectory()) walk(full);
      else if (full.endsWith('.ts')) hash.update(`${full}:${stat.mtimeMs}\n`);
    }
  };
  for (const dir of WORKER_SOURCE_DIRS) walk(path.join(srcRoot, dir));
  const key = hash.digest('hex').slice(0, 16);
  if (cachedBundle !== null && cachedBundle.key === key) return cachedBundle.url;

  const dir = path.join(os.tmpdir(), 'vroomball-rl-worker');
  fs.mkdirSync(dir, { recursive: true });
  const outfile = path.join(dir, `rl-worker-${key}.mjs`);
  if (!fs.existsSync(outfile)) {
    const esbuild = await nodeImport<typeof import('esbuild')>('esbuild');
    await esbuild.build({
      entryPoints: [path.join(here, 'rollout-worker.ts')],
      bundle: true,
      format: 'esm',
      platform: 'node',
      target: 'node18',
      outfile,
      logLevel: 'silent',
    });
  }

  const href = url.pathToFileURL(outfile).href;
  cachedBundle = { key, url: href };
  return href;
};

/**
 * A pool of `workers` threads, each owning one `RolloutCollector`.
 *
 * One worker per chunk, not a job queue: a chunk's seed is derived from its
 * INDEX, so the work is fixed at partition time and there is nothing to
 * schedule. That also means results come back in a known order, and the pool
 * produces byte-identical trajectories to `createSyncBackend` with the same
 * chunk count.
 *
 * Threads are spawned lazily on the first `collect` — building the bundle is
 * asynchronous and the factory is not.
 */
export const createWorkerBackend = (
  config: TrainingConfig,
  workers: number
): WorkerRolloutBackend => {
  const resolved = config as TrainerConfig;
  const count = Math.max(1, Math.floor(workers));
  type NodeWorker = import('node:worker_threads').Worker;

  let pool: NodeWorker[] | null = null;
  let starting: Promise<NodeWorker[]> | null = null;
  let opponent: Float64Array | null = null;
  let stats: EpisodeStats | null = null;
  let disposed = false;
  const threadIds: number[] = [];

  const start = async (): Promise<NodeWorker[]> => {
    if (pool !== null) return pool;
    if (starting === null) {
      starting = (async () => {
        const [{ threads }, entry] = await Promise.all([loadNode(), workerBundleUrl()]);
        const created: NodeWorker[] = [];
        for (let i = 0; i < count; i++) {
          created.push(
            new threads.Worker(new URL(entry), {
              workerData: { config: resolved, index: i } satisfies RolloutWorkerInit,
            })
          );
        }
        // A disposal that raced the bundle build must not leave threads holding
        // the event loop open forever.
        if (disposed) {
          for (const worker of created) void worker.terminate();
          throw new Error('rollout backend was disposed while starting');
        }
        pool = created;
        return created;
      })();
    }
    return starting;
  };

  const ask = (worker: NodeWorker, request: RolloutWorkerMessage): Promise<RolloutWorkerResponse> =>
    new Promise((resolve, reject) => {
      const onMessage = (response: RolloutWorkerResponse): void => {
        cleanup();
        if (response.error !== undefined) reject(new Error(`rollout worker failed:\n${response.error}`));
        else resolve(response);
      };
      const onError = (error: unknown): void => {
        cleanup();
        reject(error instanceof Error ? error : new Error(String(error)));
      };
      const onExit = (code: number): void => {
        cleanup();
        reject(new Error(`rollout worker exited with code ${code}`));
      };
      const cleanup = (): void => {
        worker.off('message', onMessage);
        worker.off('error', onError);
        worker.off('exit', onExit);
      };
      worker.on('message', onMessage);
      worker.on('error', onError);
      worker.on('exit', onExit);
      worker.postMessage(request);
    });

  /**
   * The pool, seen as somewhere to send half a minibatch.
   *
   * Built once and reused for the life of the backend, because the shard count
   * is part of the update's arithmetic — see `RolloutBackend.gradientShards`.
   * Null when there is nothing to gain (one worker, so the only shard would be
   * this thread's own) or nothing to build it on (no `SharedArrayBuffer`).
   *
   * The buffers are allocated on the first `begin` and reused: the batch size
   * is `stepsPerIteration` every iteration, so the reallocation branch fires
   * once per run rather than once per update.
   */
  const makeGradientShards = (): GradientShards => {
    let buffers: GradientBuffers | null = null;
    let bound: NodeWorker[] = [];
    let reports: LossReport[] = [];
    let seq = 0;

    return {
      workerShards: count,

      async begin(batch, spec: GradientSessionSpec): Promise<void> {
        if (disposed) throw new Error('gradient shards on a disposed rollout backend');
        const running = await start();
        let session = buffers;
        if (
          session === null ||
          session.count !== batch.count ||
          session.paramCount !== spec.paramCount ||
          bound !== running
        ) {
          session = allocateGradientBuffers(batch.count, spec.paramCount, count, true);
          await Promise.all(
            running.map((worker, i) =>
              ask(worker, {
                kind: 'grad-setup',
                buffers: session as GradientBuffers,
                ppo: spec.ppo,
                clipValueLoss: spec.clipValueLoss,
                hiddenSizes: [...spec.hiddenSizes],
                shard: i,
              })
            )
          );
          buffers = session;
          bound = running;
        }
        reports = [];
        publishBatch(session, batch);
      },

      publishOrder(order): void {
        if (buffers === null) throw new Error('publishOrder before begin');
        buffers.order.set(order);
      },

      async dispatch(params, from, end, norm: AdvantageNorm): Promise<void> {
        const b = buffers;
        if (b === null) throw new Error('dispatch before begin');
        // One copy of 100k doubles per minibatch — about 50 microseconds against
        // a minibatch that costs tens of milliseconds. The alternative, backing
        // the network's own parameter array with shared memory, saves that and
        // buys a whole class of aliasing bug.
        b.params.set(params);
        seq++;
        // Release. Everything written above — the parameters, and on the first
        // minibatch of an epoch the permutation and the batch — is ordered
        // against the acquiring load each shard does. See ./gradients.
        Atomics.store(b.fence, 0, seq);

        const request: GradientRequest = {
          kind: 'grad',
          start: from,
          end,
          mean: norm.mean,
          invStd: norm.invStd,
          count: norm.count,
          seq,
        };
        // Posted synchronously, before the promise is returned, so the caller's
        // own shard runs while these do.
        const pending = bound.map((worker) => ask(worker, request));
        const settled = await Promise.all(pending);
        reports = settled.map((response, i) => {
          const report = response.report;
          if (report === undefined) throw new Error(`gradient shard ${i} returned no report`);
          return report;
        });
      },

      reduce(out): LossReport {
        const b = buffers;
        if (b === null) throw new Error('reduce before begin');
        return reduceGradientShards(b, reports, out);
      },
    };
  };

  /** Built on demand and then kept, so the shard count never changes mid-run. */
  let shards: GradientShards | null = null;

  const backend: WorkerRolloutBackend = {
    threadIds,
    gradientShards() {
      // One worker means one shard, which is this thread's own — sending it away
      // and waiting would be strictly slower than doing it here.
      if (count < 2 || !sharedMemoryAvailable()) return null;
      if (shards === null) shards = makeGradientShards();
      return shards;
    },
    async collect(params, steps, seed, difficulty) {
      if (disposed) throw new Error('collect() on a disposed rollout backend');
      const running = await start();
      const budgets = splitSteps(steps, running.length);
      let done = 0;

      const responses = await Promise.all(
        running.map(async (worker, i) => {
          if (budgets[i] <= 0) return null;
          const response = await ask(worker, {
            // A copy: the buffer is structured-cloned, and handing out the live
            // parameter array would tie the clone to whatever the optimizer was
            // doing to it at that instant.
            params: Float64Array.from(params),
            steps: budgets[i],
            seed,
            opponent,
            ...(difficulty === undefined ? {} : { difficulty }),
          });
          done += budgets[i];
          backend.onProgress?.(done);
          return response;
        })
      );

      const merged = emptyEpisodeStats();
      const out: Transition[][] = [];
      for (const response of responses) {
        if (response === null) continue;
        if (response.threadId !== undefined && !threadIds.includes(response.threadId)) {
          threadIds.push(response.threadId);
        }
        for (const trajectory of response.trajectories ?? []) out.push(trajectory);
        if (response.stats !== undefined) mergeEpisodeStats(merged, response.stats);
      }
      stats = merged;
      return out;
    },
    episodeStats: () => stats,
    setOpponent(params) {
      opponent = params === null ? null : Float64Array.from(params);
    },
    dispose() {
      disposed = true;
      const running = pool;
      pool = null;
      starting = null;
      if (running !== null) for (const worker of running) void worker.terminate();
    },
  };
  return backend;
};

// ---------------------------------------------------------------------------
// Batching
// ---------------------------------------------------------------------------

/**
 * Advantages and returns per trajectory, then one flat batch.
 *
 * Per trajectory rather than over the concatenation: GAE recurses backwards and
 * would otherwise carry advantage from the start of one trajectory into the end
 * of the previous one, which is exactly the leak ./ppo §1 is about.
 *
 * The bootstrap comes from the last transition's own `bootstrap` field, which
 * the collector fills in whenever the trajectory stopped for a reason that is
 * not the episode ending — a time limit or the step budget. It is zero for a
 * genuine terminal, where `done` makes it irrelevant anyway, and zero for a
 * backend that does not report one.
 */
export const buildTrainingBatch = (
  trajectories: readonly (readonly Transition[])[],
  obsSize: number,
  gamma: number,
  lambda: number,
  rewardScale = 1
): Batch => {
  const parts: Batch[] = [];
  let count = 0;
  for (const trajectory of trajectories) {
    if (trajectory.length === 0) continue;
    const tail = trajectory[trajectory.length - 1];
    const bootstrap = tail.done ? 0 : (tail.bootstrap ?? 0);
    const part = buildBatch(trajectory, bootstrap, obsSize, gamma, lambda, rewardScale);
    parts.push(part);
    count += part.count;
  }
  if (parts.length === 0) throw new Error('no transitions to train on');
  if (parts.length === 1) return parts[0];

  const batch: Batch = {
    obs: new Float32Array(count * obsSize),
    actions: new Int32Array(count),
    logProbs: new Float64Array(count),
    advantages: new Float64Array(count),
    returns: new Float64Array(count),
    oldValues: new Float64Array(count),
    count,
  };
  let at = 0;
  for (const part of parts) {
    batch.obs.set(part.obs, at * obsSize);
    batch.actions.set(part.actions, at);
    batch.logProbs.set(part.logProbs, at);
    batch.advantages.set(part.advantages, at);
    batch.returns.set(part.returns, at);
    batch.oldValues.set(part.oldValues, at);
    at += part.count;
  }
  return batch;
};

// ---------------------------------------------------------------------------
// The trainer
// ---------------------------------------------------------------------------

export interface TrainerOptions {
  /** Resume from this checkpoint: parameters, Adam moments and statistics. */
  checkpoint?: Checkpoint;
  onProgress?: (progress: TrainerProgress) => void;
  /** Injected so the browser can supply a Web Worker backend, and so a test can
   *  supply the synchronous one without paying for threads. */
  backend?: RolloutBackend;
}

const emptyStats = (): TrainingStats => ({
  iteration: 0,
  totalSteps: 0,
  totalSeconds: 0,
  history: [],
});

/** The single wall-clock read in this file. See the header. */
const now = (): number => Date.now();

/**
 * Hand control back to the host between iterations.
 *
 * NOT cosmetic. The synchronous backend collects inside one turn and `await` on
 * an already-settled promise only queues a MICROtask, so a `run()` loop over it
 * never reaches the timer phase: a `stop()` scheduled from a `setTimeout`, a
 * click on a pause button, or a repaint would all be starved until the run
 * finished — which for `run(Infinity)` is never. One macrotask per iteration
 * costs a millisecond against an iteration measured in hundreds.
 */
const yieldToHost = (): Promise<void> =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, 0);
  });

export class Trainer {
  readonly config: TrainerConfig;

  private readonly net: MlpPolicy;
  private readonly adam: Adam;
  private readonly stats: TrainingStats;
  private readonly onProgress: ((progress: TrainerProgress) => void) | null;

  /** Non-null only when this trainer created the backend and must dispose it. */
  private ownedBackend: RolloutBackend | null = null;
  private readonly injectedBackend: RolloutBackend | null;

  /** Ring of frozen past parameter vectors, for `opponent: 'pool'`. */
  private readonly pool: Float64Array[] = [];
  private poolCursor = 0;

  /** Non-null when `ppo.normalizeReturns` is on. */
  private readonly returns: ReturnNormalizer | null;
  /** Curriculum position handed to the next collection. */
  private difficultyValue: number;
  /** Iterations since the difficulty last ROSE. See `CURRICULUM_STALL_WARNING`:
   *  a gate that is never met has to be visible, because its symptom is a long
   *  run that finishes and is merely bad. */
  private stallValue = 0;

  private phaseValue: TrainerPhase = 'idle';
  private running = false;
  private paused = false;
  private stopped = false;
  private pauseWaiters: (() => void)[] = [];
  private iterationSteps = 0;
  private stepsPerSecondValue = 0;

  constructor(config: TrainingConfig, options: TrainerOptions = {}) {
    this.config = resolveConfig(config as PartialTrainingConfig);
    this.onProgress = options.onProgress ?? null;
    this.injectedBackend = options.backend ?? null;

    const cp = options.checkpoint;
    if (cp !== undefined) {
      assertCheckpointCompatible(cp);
      if (
        cp.hiddenSizes.length !== this.config.hiddenSizes.length ||
        cp.hiddenSizes.some((h, i) => h !== this.config.hiddenSizes[i])
      ) {
        throw new Error(
          `checkpoint hiddenSizes [${cp.hiddenSizes}], config has [${this.config.hiddenSizes}]`
        );
      }
    }

    this.net = new MlpPolicy(
      OBS_SIZE,
      ACTION_COUNT,
      this.config.hiddenSizes,
      this.config.seed
    );
    this.adam = new Adam(this.net.parameterCount, {
      learningRate: this.config.ppo.learningRate,
    });
    this.returns = (this.config.ppo.normalizeReturns ?? false) ? new ReturnNormalizer() : null;
    this.difficultyValue = this.config.curriculum?.start ?? DEFAULT_CURRICULUM.start;

    if (cp !== undefined) {
      applyCheckpoint(this.net, this.adam, cp);
      if (cp.returnScale !== undefined) this.returns?.importState(cp.returnScale);
      if (cp.stats.curriculumDifficulty !== undefined) {
        this.difficultyValue = cp.stats.curriculumDifficulty;
      }
      // Carried across a resume for the same reason the difficulty is: a run
      // restarted every few hours would reset the stall counter each time and
      // never reach the threshold that reports the deadlock.
      this.stallValue = cp.stats.curriculumStall ?? 0;
      // A schedule is a function of the steps consumed, so it is RECOMPUTED on
      // resume rather than restored. Restoring would pin a resumed run to the
      // ramp the original was given, silently ignoring a changed `--ramp-steps`
      // — and the recomputed value is identical when nothing changed.
      if ((this.config.curriculum?.mode ?? DEFAULT_CURRICULUM.mode) === 'scheduled') {
        this.difficultyValue = scheduledDifficulty(cp.stats.totalSteps, this.config.curriculum);
      }
      this.stats = {
        iteration: cp.stats.iteration,
        totalSteps: cp.stats.totalSteps,
        totalSeconds: cp.stats.totalSeconds,
        history: trimHistory([...cp.stats.history]),
        curriculumDifficulty: this.difficultyValue,
        curriculumStall: this.stallValue,
      };
    } else {
      this.stats = emptyStats();
      this.stats.curriculumDifficulty = this.difficultyValue;
      this.stats.curriculumStall = 0;
    }

    if ((this.config.opponent?.kind ?? 'self') === 'pool') {
      this.pool.push(Float64Array.from(this.net.getParameters()));
    }
  }

  /** Resume, deriving the network shape from the checkpoint. */
  static fromCheckpoint(cp: Checkpoint, config: PartialTrainingConfig = {}): Trainer {
    assertCheckpointCompatible(cp);
    const merged = resolveConfig({
      ...config,
      hiddenSizes: config.hiddenSizes ?? cp.hiddenSizes,
    });
    return new Trainer(merged, { checkpoint: cp });
  }

  get progress(): TrainerProgress {
    return {
      phase: this.phaseValue,
      stats: this.stats,
      iterationProgress: this.iterationSteps,
      stepsPerSecond: this.stepsPerSecondValue,
    };
  }

  get phase(): TrainerPhase {
    return this.phaseValue;
  }

  /** The live network, for a UI that wants to run the current policy without
   *  round-tripping a checkpoint. Read-only by convention. */
  get network(): MlpPolicy {
    return this.net;
  }

  // -------------------------------------------------------------------------
  // Control
  // -------------------------------------------------------------------------

  /**
   * Run `iterations` PPO updates, or until `stop()`.
   *
   * Resolves after exactly `iterations` updates when it runs to completion, and
   * as soon as the in-flight collection returns when it is stopped — a stop
   * between collecting and updating discards that batch rather than applying
   * half an iteration, so `totalSteps` only ever counts steps that trained.
   */
  async run(iterations = Number.POSITIVE_INFINITY): Promise<void> {
    if (this.running) throw new Error('run() is already in progress');
    this.running = true;
    this.stopped = false;
    let completed = 0;

    try {
      while (completed < iterations && !this.stopped) {
        await yieldToHost();
        await this.waitWhilePaused();
        if (this.stopped) break;

        const started = now();
        const backend = this.backend();
        backend.onProgress = (steps) => {
          this.iterationSteps = steps;
        };

        this.setPhase('collecting');
        this.iterationSteps = 0;
        this.applyOpponent(backend);

        const seed = mixSeed(this.config.seed, this.stats.iteration);
        const trajectories = await backend.collect(
          this.net.getParameters(),
          this.config.stepsPerIteration,
          seed,
          this.difficultyValue
        );
        if (this.stopped) break;

        this.setPhase('updating');
        this.iterationSteps = this.config.stepsPerIteration;
        await this.update(trajectories, backend.episodeStats?.() ?? null, backend);

        const elapsed = Math.max(1e-6, (now() - started) / 1000);
        this.stats.totalSeconds += elapsed;
        this.stepsPerSecondValue = this.config.stepsPerIteration / elapsed;

        completed++;
        this.onProgress?.(this.progress);
      }
    } finally {
      this.running = false;
      this.setPhase(this.stopped ? 'stopped' : 'idle');
    }
  }

  /** Takes effect at the next iteration boundary — the collection already in
   *  flight finishes, because abandoning it would waste the work AND leave the
   *  step accounting owing a partial iteration. */
  pause(): void {
    if (this.stopped) return;
    this.paused = true;
    this.setPhase('paused');
  }

  resume(): void {
    if (!this.paused) return;
    this.paused = false;
    this.drainPauseWaiters();
    this.setPhase(this.running ? 'collecting' : 'idle');
  }

  /**
   * Stop, and release whatever this trainer allocated.
   *
   * A stopped trainer is still usable: `run()` recreates the backend it owns.
   * An INJECTED backend is never disposed here — its owner made it and its
   * owner ends it.
   */
  stop(): void {
    this.stopped = true;
    this.paused = false;
    this.drainPauseWaiters();
    this.disposeOwnedBackend();
    this.setPhase('stopped');
  }

  /** Release the backend this trainer owns without marking the run stopped. */
  dispose(): void {
    this.disposeOwnedBackend();
  }

  // -------------------------------------------------------------------------
  // Checkpoints
  // -------------------------------------------------------------------------

  /**
   * A snapshot safe to save, or to hand to `LearnedPolicy`, while training
   * continues: every array is copied, and the history is trimmed here rather
   * than at save time so the copy cannot be the large one.
   */
  toCheckpoint(label?: string): Checkpoint {
    const stats: TrainingStats = {
      iteration: this.stats.iteration,
      totalSteps: this.stats.totalSteps,
      totalSeconds: this.stats.totalSeconds,
      history: trimHistory(this.stats.history.map((entry) => ({ ...entry }))),
      curriculumDifficulty: this.difficultyValue,
      curriculumStall: this.stallValue,
    };
    const checkpoint = toCheckpoint(this.net, this.adam, stats, label);
    // Added here rather than inside `toCheckpoint`: the return scale belongs to
    // the training LOOP, not to the network, and ./nn knows nothing about it.
    // Without it a resumed run divides by a rebuilt estimate and diverges from
    // the straight run, which `test/rl/trainer.test.ts` checks parameter for
    // parameter.
    if (this.returns !== null) checkpoint.returnScale = this.returns.exportState();
    return checkpoint;
  }

  /** Where the curriculum currently is, in [0, 1]. */
  get difficulty(): number {
    return this.difficultyValue;
  }

  /** Iterations since the difficulty last rose. Zero on a run that has just
   *  been promoted, and climbing on one whose gate is never met. */
  get curriculumStall(): number {
    return this.stallValue;
  }

  // -------------------------------------------------------------------------

  private backend(): RolloutBackend {
    if (this.injectedBackend !== null) return this.injectedBackend;
    if (this.ownedBackend === null) this.ownedBackend = createSyncBackend(this.config);
    return this.ownedBackend;
  }

  private disposeOwnedBackend(): void {
    if (this.ownedBackend === null) return;
    this.ownedBackend.dispose();
    this.ownedBackend = null;
  }

  private setPhase(phase: TrainerPhase): void {
    this.phaseValue = phase;
  }

  private drainPauseWaiters(): void {
    const waiters = this.pauseWaiters;
    this.pauseWaiters = [];
    for (const resolve of waiters) resolve();
  }

  private async waitWhilePaused(): Promise<void> {
    while (this.paused && !this.stopped) {
      this.setPhase('paused');
      await new Promise<void>((resolve) => {
        this.pauseWaiters.push(resolve);
      });
    }
  }

  /**
   * Refresh the frozen opponent for `opponent: 'pool'`.
   *
   * The snapshot taken every `refreshIterations` goes into a bounded ring, and
   * the iteration index picks which member plays. A ring rather than "the last
   * snapshot" because a policy that only ever faces its immediate past can
   * still cycle with it; a policy that faces something from twenty iterations
   * ago cannot.
   */
  private applyOpponent(backend: RolloutBackend): void {
    const opponent = this.config.opponent ?? { kind: 'self' };
    if (opponent.kind !== 'pool' || backend.setOpponent === undefined) return;

    const every = Math.max(1, Math.floor(opponent.refreshIterations ?? 20));
    const size = Math.max(1, Math.floor(opponent.size ?? 4));
    if (this.stats.iteration > 0 && this.stats.iteration % every === 0) {
      const snapshot = Float64Array.from(this.net.getParameters());
      if (this.pool.length < size) this.pool.push(snapshot);
      else {
        this.pool[this.poolCursor] = snapshot;
        this.poolCursor = (this.poolCursor + 1) % size;
      }
    }
    backend.setOpponent(this.pool[this.stats.iteration % this.pool.length]);
  }

  /**
   * Whether this iteration's update runs sharded, and on what.
   *
   * Null every time unless the run asked for it AND the backend can offer it,
   * which is the property that makes the flag safe to add: an unset flag, a
   * synchronous backend, a single worker and a browser all take exactly the
   * code path they took before this existed.
   */
  private gradientShards(backend: RolloutBackend): GradientShards | null {
    if (this.config.parallelGradients !== true) return null;
    return backend.gradientShards?.() ?? null;
  }

  /** One PPO update on the collected experience, and the statistics for it. */
  private async update(
    trajectories: readonly Transition[][],
    episodes: EpisodeStats | null,
    backend: RolloutBackend
  ): Promise<void> {
    // The return scale is updated from THIS iteration's rewards and then used on
    // them, rather than lagging one iteration behind. A batch normalized by a
    // number estimated before it was collected is a batch normalized by the
    // wrong number exactly when the reward distribution moves, which is exactly
    // when it matters — the iteration the policy first scores.
    const scale = this.updateReturnScale(trajectories);
    const batch = buildTrainingBatch(
      trajectories,
      OBS_SIZE,
      this.config.ppo.gamma,
      this.config.ppo.lambda,
      scale
    );

    // Constructed per iteration on purpose — see the file header. The seed is a
    // function of the iteration index, which a checkpoint carries.
    const ppo = new PPO(this.net, this.adam, this.config.ppo, {
      seed: mixSeed(this.config.seed, this.stats.iteration + 1),
    });
    const shards = this.gradientShards(backend);
    const report = shards === null ? ppo.update(batch) : await ppo.updateParallel(batch, shards);

    this.stats.iteration++;
    this.stats.totalSteps += batch.count;

    const episodeCount = episodes === null ? 0 : Math.max(1, episodes.episodes);
    const rewardTerms: Record<string, number> = {};
    if (episodes !== null) {
      for (const [name, value] of Object.entries(episodes.rewardTermSum)) {
        rewardTerms[name] = value / episodeCount;
      }
    }

    const entry: IterationStats = {
      iteration: this.stats.iteration,
      meanReturn: episodes === null ? 0 : episodes.returnSum / episodeCount,
      meanEpisodeLength: episodes === null ? 0 : episodes.lengthTickSum / episodeCount,
      goalsFor: episodes === null ? 0 : episodes.goalsFor / episodeCount,
      goalsAgainst: episodes === null ? 0 : episodes.goalsAgainst / episodeCount,
      touchRate: episodes === null ? 0 : episodes.touchedEpisodes / episodeCount,
      // Alongside `touchRate`, never instead of it: the UI charts that field and
      // tests assert on it, and the two answer different questions. This is the
      // one the gate reads — see `CurriculumConfig`.
      episodeTouchRate: episodes === null ? 0 : gateTouchRate(episodes),
      policyLoss: report.policyLoss,
      valueLoss: report.valueLoss,
      entropy: report.entropy,
      approxKl: report.approxKl,
      clipFraction: report.clipFraction,
      rewardTerms,
      difficulty: this.difficultyValue,
      curriculumStall: this.stallValue,
    };
    this.stats.history.push(entry);
    trimHistory(this.stats.history);

    // Measured, not scheduled — see `CurriculumConfig`. Read AFTER the entry is
    // written so the history records the difficulty the data was collected at,
    // not the one the next iteration will use.
    if (this.usesCurriculum) {
      const before = this.difficultyValue;
      this.difficultyValue =
        (this.config.curriculum?.mode ?? DEFAULT_CURRICULUM.mode) === 'scheduled'
          ? // Read from the step total AFTER this iteration's batch was added to
            // it, so the difficulty handed to the next collection describes the
            // experience the policy has actually had.
            scheduledDifficulty(this.stats.totalSteps, this.config.curriculum)
          : nextDifficulty(
              this.difficultyValue,
              entry.episodeTouchRate ?? entry.touchRate,
              this.stats.iteration,
              this.config.curriculum
            );
      // A promotion, and only a promotion, resets the counter. A retreat is the
      // gate working; a run that only ever retreats is as stuck as one that
      // never moves, and both should show a rising number here.
      this.stallValue = this.difficultyValue > before ? 0 : this.stallValue + 1;
      this.stats.curriculumDifficulty = this.difficultyValue;
      this.stats.curriculumStall = this.stallValue;
    }
  }

  /** Does this run have a curriculum to advance? A fixed reset mode does not. */
  private get usesCurriculum(): boolean {
    const mode = this.config.resetMode ?? 'curriculum';
    return mode === 'curriculum' || mode === 'nearBall';
  }

  /** Fold an iteration's rewards into the return-scale estimate and return the
   *  factor `buildTrainingBatch` should multiply rewards by. 1 when off. */
  private updateReturnScale(trajectories: readonly (readonly Transition[])[]): number {
    const normalizer = this.returns;
    if (normalizer === null) return 1;
    const gamma = this.config.ppo.gamma;
    for (const trajectory of trajectories) {
      if (trajectory.length === 0) continue;
      const rewards = new Float64Array(trajectory.length);
      const dones: boolean[] = new Array(trajectory.length);
      for (let i = 0; i < trajectory.length; i++) {
        rewards[i] = trajectory[i].reward;
        dones[i] = trajectory[i].done;
      }
      // Each trajectory starts its own accumulator: a trajectory is one
      // episode's worth of steps (see the `RolloutBackend` contract), and
      // carrying the accumulator between two of them would describe a return
      // that no episode ever produced.
      normalizer.observe(rewards, dones, gamma);
    }
    return 1 / normalizer.scale;
  }
}

/** Drop the oldest entries in place. An unbounded history makes a checkpoint
 *  grow without limit over a run left going for hours. */
const trimHistory = (history: IterationStats[]): IterationStats[] => {
  if (history.length > HISTORY_LIMIT) history.splice(0, history.length - HISTORY_LIMIT);
  return history;
};
